import { ChevronDown } from 'lucide-react';

import {
  coverage,
  criteriaOf,
  inventory,
  MANUAL_CRITERIA,
} from '../../content/checks';
import { Container, SectionHeading } from '../ui';

const LEVEL_LABEL: Record<string, string> = {
  A: 'A',
  AA: 'AA',
  AAA: 'AAA advisory',
  'best-practice': 'Best practice',
};

export function WhatWeCheck() {
  return (
    <section
      aria-labelledby="checks-title"
      className="py-24 md:py-32"
      id="what-we-check"
    >
      <Container>
        <SectionHeading
          eyebrow="What we check"
          id="checks-title"
          intro={`${coverage.checks} checks on every page, then a specialist verifies each finding.`}
          title="Far more than a scan"
        />

        <ul className="mt-12 grid grid-cols-1 gap-x-10 border-t border-hairline sm:grid-cols-2 lg:grid-cols-3">
          {inventory.categories.map((cat) => (
            <li
              className="flex items-baseline justify-between gap-4 border-b border-hairline py-4"
              key={cat.id}
            >
              <span className="text-ui-lg font-medium text-text-primary">
                {cat.name}
              </span>
              <span className="shrink-0 text-ui tabular-nums text-text-secondary">
                {cat.checks.length}
              </span>
            </li>
          ))}
        </ul>

        <p className="mt-6 max-w-3xl text-ui text-text-secondary">
          Plus {MANUAL_CRITERIA.length} criteria a specialist assesses by hand,
          such as captions, flashing content and consistent navigation.
        </p>
        <p className="mt-2 max-w-3xl text-ui text-text-secondary">
          Covers the web requirements of EN 301 549 (clause 9), the European
          standard behind the European Accessibility Act.
        </p>

        <details className="group mt-8">
          <summary className="inline-flex cursor-pointer list-none items-center gap-1.5 rounded-button text-ui-strong font-semibold text-text-primary hover:underline [&::-webkit-details-marker]:hidden">
            See all {coverage.checks} checks
            <ChevronDown
              aria-hidden
              className="transition-transform group-open:rotate-180"
              size={16}
            />
          </summary>
          <div className="mt-6 grid gap-x-12 gap-y-10 md:grid-cols-2">
            {inventory.categories.map((cat) => {
              const criteria = criteriaOf(cat);
              return (
                <div key={cat.id}>
                  <h3 className="text-subtitle font-semibold text-text-primary">
                    {cat.name}
                  </h3>
                  {criteria.length > 0 ? (
                    <p className="mt-1 text-caption text-text-secondary">
                      WCAG {criteria.join(' · ')}
                    </p>
                  ) : null}
                  <ul className="mt-3 divide-y divide-hairline border-t border-hairline">
                    {cat.checks.map((check) => (
                      <li
                        className="flex items-start justify-between gap-4 py-2 text-ui text-text-primary"
                        key={`${check.title}-${check.wcag.join()}`}
                      >
                        <span>{check.title}</span>
                        <span className="shrink-0 text-caption text-text-secondary">
                          {check.wcag.length > 0
                            ? `${check.wcag.join(', ')} · `
                            : ''}
                          {LEVEL_LABEL[check.level] ?? check.level}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              );
            })}
          </div>
        </details>
      </Container>
    </section>
  );
}
